import React, { useState, useRef, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import ReactMarkdown from 'react-markdown';
import { Upload, FileUp, Send, Trash2, Loader2, LogOut } from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';

interface Message {
  text: string;
  sender: 'user' | 'bot';
}

interface DocumentHistory {
  id: number;
  filename: string;
  uploaded_at: string;
}

interface AppProps {
  onSidebarToggle: () => void;
  selectedDocument: DocumentHistory | null;
  setSelectedDocument: (doc: DocumentHistory | null) => void;
  onNewDocumentUploaded: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const App: React.FC<AppProps> = ({ onSidebarToggle, selectedDocument, setSelectedDocument, onNewDocumentUploaded }) => {
  const { keycloak, initialized } = useKeycloak();
  const [file, setFile] = useState<File | null>(null);
  const [messages, setMessages] = useState<Message[]>([
    {
      sender: 'bot',
      text: '¡Hola! Soy *DocuChat AI*. Puedes subir un archivo PDF y luego preguntarme cualquier cosa relacionada con su contenido.',
    },
  ]);
  const [input, setInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const username = keycloak.tokenParsed?.preferred_username || 'Usuario';

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  useEffect(() => {
    const fetchConversationHistory = async () => {
      if (!selectedDocument) {
        setMessages([
          {
            sender: 'bot',
            text: '¡Hola! Soy *DocuChat AI*. Puedes subir un archivo PDF y luego preguntarme cualquier cosa relacionada con su contenido.',
          },
        ]);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/conversation`, {
          params: { document_id: selectedDocument.id },
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });
        const conversation = response.data.map((msg: { message: string; sender: string }) => ({
          text: msg.message,
          sender: msg.sender,
        }));
        
        if (conversation.length === 0) {
          setMessages([
            {
              sender: 'bot',
              text: `¡Hola! Ahora puedes preguntarme sobre el contenido de "${selectedDocument.filename}".`,
            },
          ]);
        } else {
          setMessages(conversation);
        }
      } catch (error) {
        console.error('Error al cargar la conversación:', error);
        setMessages([{ text: 'Error al cargar la conversación.', sender: 'bot' }]);
      }
    };
    fetchConversationHistory();
  }, [selectedDocument, keycloak.token]);

  if (!initialized) {
    return <div>Cargando...</div>;
  }

  if (!keycloak.authenticated) {
    return (
      <div className="chat-container">
        <header className="chat-header">
          <h1>DocuChat AI</h1>
        </header>
        <div className="chat-messages">
          <div className="message-bubble bot-message">
            <p>Por favor, inicia sesión para acceder a DocuChat AI.</p>
            <button onClick={() => keycloak.login()} className="action-button login-button">
              Iniciar Sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleUpload = async () => {
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setIsUploading(true);
    const uploadedFilename = file.name;
    setFile(null);
    setSelectedDocument(null);

    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${keycloak.token}`,
        },
      });
      onNewDocumentUploaded();
    } catch (error) {
      let errorMessage = 'Error al subir e indexar el archivo. Intenta de nuevo.';
      
      if (axios.isAxiosError(error) && error.response) {
        console.error("Respuesta de error del servidor:", error.response.data);
        if (error.response.status === 400 && error.response.data && typeof error.response.data === 'object' && 'detail' in error.response.data) {
          errorMessage = (error.response.data as { detail: string }).detail;
        } else {
          errorMessage = `Error ${error.response.status}: ${error.response.statusText}`;
        }
      } else {
        console.error("Error no HTTP:", error);
        errorMessage = 'Error de red. Por favor, verifica tu conexión.';
      }
      
      setMessages((prev) => [...prev, { text: errorMessage, sender: 'bot' }]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isSending) return;

    const userQuestion = input;
    setMessages((prev) => [...prev, { text: userQuestion, sender: 'user' }]);
    setInput('');
    setIsSending(true);

    try {
      const payload = {
        question: userQuestion,
        document_id: selectedDocument?.id || null,
      };

      const response = await axios.post(
        `${API_URL}/ask`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
          },
        }
      );
      setMessages((prev) => [...prev, { text: response.data.answer, sender: 'bot' }]);
    } catch (error) {
      const errorMessage = 'Error al procesar la pregunta. Intenta de nuevo.';
      setMessages((prev) => [...prev, { text: errorMessage, sender: 'bot' }]);
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && input.trim()) {
      handleSend();
    }
  };

  return (
    <div className="chat-container">
      <header className="chat-header">
        <div className="header-content">
          <button className="hamburger-menu" onClick={onSidebarToggle} aria-label="Toggle Sidebar">
            ☰
          </button>
        </div>
      </header>
      <div ref={chatContainerRef} className="chat-messages">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`message-bubble ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
          >
            <ReactMarkdown>{message.text}</ReactMarkdown>
          </div>
        ))}
      </div>
      <div className="chat-input-area">
        <div className="input-group">
          <div className="file-actions-row">
            <div className="file-actions">
              {isUploading ? (
                <div className="file-info-container">
                  <span className="file-name">Procesando...</span>
                  <Loader2 size={16} className="animate-spin" />
                </div>
              ) : !file ? (
                <>
                  <label htmlFor="file-upload" className="file-upload-label">
                    <FileUp size={20} />
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="file-upload-input"
                  />
                </>
              ) : (
                <div className="file-info-container">
                  <span className="file-name">{file.name}</span>
                  <button
                    onClick={handleRemoveFile}
                    className="file-remove-btn"
                    title="Eliminar archivo"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handleUpload}
              className="action-button upload-button"
              disabled={!file || isUploading}
              title="Indexar archivo"
            >
              {isUploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
            </button>
          </div>
          <div className="send-actions-row">
            <input
              type="text"
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu pregunta..."
              disabled={isSending || isUploading}
            />
            <button
              onClick={handleSend}
              className="action-button send-button"
              disabled={!input.trim() || isSending || isUploading}
              title="Enviar pregunta"
            >
              {isSending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;